import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "../../projectionContract";

/**
 * AW-ORIGINAL-RESTORE-001 -- the fail-closed guard the original tool
 * panels needed in order to become Production-eligible.
 *
 * WHAT CHANGED, AND WHY IT HAD TO
 * -------------------------------
 * Each panel used to default its data props to its own fixture and its
 * `projectionStatus` to `ok`. Both defaults were reasonable in a shell
 * that only ever rendered fixtures, and both are unsafe anywhere else:
 * a caller that forgot a prop, or handed over a projection the
 * contract had already rejected, got a confident render of synthetic
 * customer data.
 *
 * So the defaults are gone and this decides what a panel renders
 * instead. There is no implicit fixture fallback left anywhere in the
 * panel tier.
 *
 * THE RULE
 * --------
 * A panel renders data only when BOTH halves hold:
 *
 *   1. an explicit projection status of `ok` -- which
 *      `resolveWorkspaceToolProjection` already withholds for a
 *      missing, stale, restricted, conflicting, pending or
 *      cross-account projection, and for one whose fields fail the
 *      allowlist; and
 *   2. every required field actually present on the payload.
 *
 * Either half failing yields a status the panel surfaces through
 * `ToolProjectionNotice`, never a partial render. The second half
 * matters more than it looks: the contract's own resolver returns
 * `{status: "ok", data: null}` for nothing, but a caller composing a
 * projection by hand can hand over `ok` with half a payload, and
 * "display what arrived" is exactly how a blank Address beside a real
 * name gets read as "this prospect has no address".
 *
 * WHY `undefined` AND NOT FALSY
 * -----------------------------
 * `required` is checked for presence, not truthiness. An empty array of
 * notes and an empty object of lead information are legitimate,
 * authoritative answers -- "this lead has no notes" is a fact the
 * backend may assert. Only an absent key means the projection never
 * carried the field.
 *
 * Imports `projectionContract` and nothing else; that module imports
 * nothing at all. No fixture, no runtime, no network.
 */

/** The status a panel renders when no projection was supplied at all. */
export const PROJECTION_ABSENT = Object.freeze({
  status: WORKSPACE_TOOL_PROJECTION_STATUSES.MISSING,
  reason: "projection_absent",
});

/** The status a panel renders when the projection claims `ok` but did
 *  not carry every field the panel needs to render honestly. */
export const PROJECTION_INCOMPLETE = Object.freeze({
  status: WORKSPACE_TOOL_PROJECTION_STATUSES.MISSING,
  reason: "projection_incomplete",
});

/**
 * Decide what a tool panel should render.
 *
 * @param projectionStatus the caller's `{status, reason}`, or nothing
 * @param required         the panel's required payload values, in any
 *                         order; presence is what is checked
 * @returns `{status, reason}` -- `ok` only when the panel may render
 */
export function resolveToolRenderStatus(projectionStatus, required = []) {
  if (!projectionStatus || typeof projectionStatus.status !== "string") {
    return PROJECTION_ABSENT;
  }
  if (projectionStatus.status !== WORKSPACE_TOOL_PROJECTION_STATUSES.OK) {
    return projectionStatus;
  }
  if (required.some((value) => value === undefined || value === null)) {
    return PROJECTION_INCOMPLETE;
  }
  return projectionStatus;
}

/**
 * AW-LDQ-003. The ONE place a Production surface may obtain an `ok`
 * status, and the only reason it is allowed to exist.
 *
 * The panels render data only on `ok`, so something must produce one.
 * Letting each call site write `{ status: "ok" }` is exactly the bypass
 * the source guard forbids -- a literal is indistinguishable from an
 * earned verdict, and a careless edit turns withheld data into rendered
 * data with no trace.
 *
 * So the verdict is produced here, by a function whose name states its
 * precondition, and the precondition is a real one: a run-scoped
 * endpoint returned 2xx. That is not a weaker check than it sounds.
 * Every `/runs/{id}/…` read resolves its run through
 * `get_account_bound_run` BEFORE anything else -- so a 2xx means the
 * server has already established that the run exists inside the
 * caller's selected Account and that the caller owns it or holds
 * Account-admin authority there. A 404 or 403 never reaches this
 * function.
 *
 * It is therefore a *relay* of a server-side authorization decision,
 * not a browser-side one. The browser cannot do better than relay, and
 * pretending otherwise by validating an envelope it also constructed
 * would be theatre.
 *
 * Call it with a successful run-scoped response and nothing else.
 */
export function authorizedByServer() {
  return Object.freeze({
    status: WORKSPACE_TOOL_PROJECTION_STATUSES.OK,
    reason: null,
  });
}

/**
 * A withheld verdict carrying its own reason, for every outcome that is
 * not a successful authorized read: a transport failure, a 403, a 409
 * from a legacy snapshot, or simply "no run is current".
 */
export function withheld(status, reason = null) {
  return Object.freeze({ status, reason });
}

/** True when `resolveToolRenderStatus` admitted a render. */
export function mayRender(renderStatus) {
  return renderStatus.status === WORKSPACE_TOOL_PROJECTION_STATUSES.OK;
}
